import classNames from 'classnames';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Affix from '../../wui-affix/src'
import Icon from '../../wui-icon/src';
import {createChainedFunction} from '../../wui-core/src';
import {getNid, WebUI} from "../../wui-core/src/index"
import { AntdAnchorProps } from './iAnchor'
import AntdAnchorLink from './AntdAnchorLink';

const defaultProps = {
    prefixCls: 'wui-anchor',
    affix: true,
    offsetTop: 0,
    showInkInFixed: false,
    onChange: null,
    onClick: null,
    collapsable: false,
    defaultExpanded: true,
};

const AntdAnchor: React.FC<AntdAnchorProps> = (incomingProps) => {
    // ==================== Props ====================
    const props = { ...defaultProps, ...incomingProps };
    const {
        children, items, activeKey: propActiveKey, prefixCls,
        affix, offsetTop, getContainer, showInkInFixed, fieldid,
        style, className = '', onClick, onChange, collapsable,
        defaultExpanded, collapsableDir = 'left', onCollapsable, targetOffset
    } = props;

    // ==================== State ====================
    const [activeKey, setActiveKey] = useState<string | undefined>(propActiveKey);
    const [isFix, setIsFix] = useState<boolean>(defaultExpanded ?? true);

    // ==================== Refs ====================
    const instanceRefs = useRef({
        hrefArr: [] as string[],
        isScroll: true
    });

    const outBoxRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const spaceDivRef = useRef<HTMLDivElement>(null);

    // ==================== 事件处理 ====================
    const scrollHand = useCallback(() => {
        if (!instanceRefs.current.isScroll) return;

        const scrollOffset = targetOffset ?? (offsetTop || 0)

        instanceRefs.current.hrefArr.forEach((item) => {
            const element = document.getElementById(item);
            if (!element) return;

            const rect = element.getBoundingClientRect();

            if (getContainer === undefined) {
                // 应用偏移量：元素顶部位置需要考虑 scrollOffset
                if (-rect.height < rect.y - scrollOffset && rect.y - scrollOffset <= 0) {
                    setActiveKey(item);
                }
            } else {
                const containerRect = getContainer()?.getBoundingClientRect();
                if (containerRect && rect.y - scrollOffset <= containerRect.y) {
                    setActiveKey(item);
                }
            }
        });

        setTimeout(() => {
            instanceRefs.current.isScroll = true;
        }, 10);
    }, [getContainer, targetOffset, offsetTop]);

    const handleSelect = useCallback((val: Record<string, any>) => {
        setActiveKey(val.href);
        instanceRefs.current.isScroll = false;

        // 应用 targetOffset 进行滚动
        const targetElement = document.getElementById(val.href);
        if (targetElement) {
            const scrollOffset = targetOffset ?? (offsetTop || 0)
            const container = getContainer?.() || window;

            if (container === window) {
                const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                    top: elementTop - scrollOffset,
                    behavior: 'auto'
                });
            } else {
                const containerElement = container as HTMLElement;
                const elementTop = targetElement.offsetTop;
                containerElement.scrollTo({
                    top: elementTop - scrollOffset,
                    behavior: 'auto'
                });
            }
        }

        if (onClick) onClick(val);
        if (onChange) onChange(val.href);
        setTimeout(() => {
            instanceRefs.current.isScroll = true;
        }, 10)
    }, [onClick, onChange, targetOffset, offsetTop, getContainer]);

    const fixHandle = useCallback(() => {
        setIsFix(prev => {
            const newValue = !prev;
            onCollapsable?.(newValue);
            return newValue;
        });
    }, [onCollapsable]);

    const toTopHandle = useCallback(() => {
        let firstHref: string = '';

        if (items && items.length > 0) {
            firstHref = items[0]?.href;
        } else if (children && React.Children.count(children) > 0) {
            const firstChild = React.Children.toArray(children)[0] as any;
            firstHref = firstChild?.props?.href;
        }

        if (firstHref) {
            setActiveKey(firstHref);
            const targetElement = document.getElementById(firstHref);

            if (targetElement) {
                // 应用 targetOffset 偏移量
                const scrollOffset = targetOffset ?? (offsetTop || 0)
                const container = getContainer?.() || window;

                if (container === window) {
                    const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
                    window.scrollTo({
                        top: elementTop - scrollOffset,
                        behavior: 'auto'
                    });
                } else {
                    const containerElement = container as HTMLElement;
                    const elementTop = targetElement.offsetTop;
                    containerElement.scrollTo({
                        top: elementTop - scrollOffset,
                        behavior: 'auto'
                    });
                }
            }
        }
    }, [items, children, targetOffset, offsetTop, getContainer]);

    // ==================== Effects ====================
    // 初始化
    useEffect(() => {
        // 设置 hrefArr 和初始 activeKey
        if (items && items.length > 0) {
            instanceRefs.current.hrefArr = items.map(item => item.href);
            if (!propActiveKey) {
                setActiveKey(items[0]?.href);
            }
        } else if (children) {
            const hrefs: string[] = [];
            React.Children.forEach(children, (child: any, index) => {
                hrefs.push(child.props.href);
                if (index === 0 && !propActiveKey) {
                    setActiveKey(child.props.href);
                }
            });
            instanceRefs.current.hrefArr = hrefs;
        }

        // 添加滚动监听
        if (getContainer === undefined) {
            document.addEventListener('scroll', scrollHand);
        } else {
            setTimeout(() => {
                getContainer()?.addEventListener('scroll', scrollHand);
            }, 0);
        }

        // 计算折叠模式空白高度
        if (collapsable && outBoxRef.current && containerRef.current && spaceDivRef.current) {
            const outBoxHeight = outBoxRef.current.getBoundingClientRect().height;
            const containerHeight = containerRef.current.getBoundingClientRect().height;
            spaceDivRef.current.style.cssText = `height: ${outBoxHeight - containerHeight - 16 - 50.4}px`;
        }

        // 清理
        return () => {
            if (getContainer === undefined) {
                document.removeEventListener('scroll', scrollHand);
            } else {
                getContainer()?.removeEventListener('scroll', scrollHand);
            }
        };
    }, []);

    // 监听 propActiveKey 变化
    useEffect(() => {
        if (propActiveKey && propActiveKey !== activeKey) {
            setActiveKey(propActiveKey);
            const targetElement = document.getElementById(propActiveKey);

            if (targetElement) {
                // 应用 targetOffset 偏移量
                const scrollOffset = targetOffset ?? (offsetTop || 0)
                const container = getContainer?.() || window;

                if (container === window) {
                    const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
                    window.scrollTo({
                        top: elementTop - scrollOffset,
                        behavior: 'smooth'
                    });
                } else {
                    const containerElement = container as HTMLElement;
                    const elementTop = targetElement.offsetTop;
                    containerElement.scrollTo({
                        top: elementTop - scrollOffset,
                        behavior: 'smooth'
                    });
                }
            }
        }
    }, [propActiveKey, activeKey, targetOffset, offsetTop, getContainer]);

    // ==================== 渲染逻辑 ====================
    const handleItems = useMemo(() => {
        const linkArr: any[] = [];

        if (items && items.length > 0) {
            items.forEach((item: { key: string, href: string, title: string }) => {
                linkArr.push(
                    <AntdAnchorLink
                        expanded={item.href === activeKey}
                        fieldid={fieldid}
                        onSelect={handleSelect}
                        href={item.href}
                        title={item.title}
                        key={item.key}
                    />
                );
            });
        } else {
            React.Children.forEach(children, (child: React.ReactElement) => {
                const linkHref = child.props.href;
                const childProps = {
                    onscrollTo: undefined,
                    eventKey: linkHref,
                    expanded: linkHref === activeKey,
                    onSelect: createChainedFunction(handleSelect, child.props.onSelect),
                    fieldid
                };
                linkArr.push(React.cloneElement(child, childProps));
            });
        }

        return linkArr;
    }, [items, children, activeKey, handleSelect, fieldid]);

    const adapterNid = getNid(props);

    const wrapperClass = classNames({
        [`${prefixCls}-wrapper`]: true,
        [`${className}`]: className,
        [`${prefixCls}-collapsable`]: collapsable,
        [`${prefixCls}-collapsabled`]: isFix,
        [`${prefixCls}-collapsable-handle`]: !isFix,
        [`${collapsableDir}`]: !isFix
    });

    const collapsableClass = classNames({
        [`${prefixCls}-collapsable-footer`]: true,
    });

    const wrapperStyle = {
        maxHeight: offsetTop ? `calc(100vh - ${offsetTop}px)` : '100vh',
        ...style,
    };

    const anchorClass = classNames(prefixCls, {
        fixed: !affix && !showInkInFixed,
    });

    const anchorContent = (
        <div className={wrapperClass} style={wrapperStyle}>
            <div className={anchorClass} ref={containerRef}>
                <div className={`${prefixCls}-ink`}></div>
                {handleItems}
            </div>
            {collapsable && <div ref={spaceDivRef}></div>}
            {collapsable && (
                <div className={collapsableClass}>
                    {collapsableDir === 'left' ? (
                        isFix ? <Icon type="uf-daoshouye" onClick={fixHandle} /> : <Icon type="uf-youjiantou_shuang" onClick={fixHandle}></Icon>
                    ) : (
                        isFix ? <Icon type="uf-youjiantou_shuang" onClick={fixHandle} /> : <Icon type="uf-daoshouye" onClick={fixHandle}></Icon>
                    )}
                    {isFix && <Icon onClick={toTopHandle} type="uf-top-up" />}
                </div>
            )}
        </div>
    );

    return (
        <div {...adapterNid} fieldid={fieldid} className={`${prefixCls}-wrapper-outer`} ref={outBoxRef}>
            {!affix ? (
                anchorContent
            ) : (
                <Affix offsetTop={offsetTop} target={getContainer}>
                    {anchorContent}
                </Affix>
            )}
        </div>
    );
};

export default WebUI({ name: "anchor", defaultProps })(AntdAnchor);
